import React from 'react';
import {Box, SxProps, Typography, useMediaQuery} from '@mui/material';
import {PrimaryBigButton} from '../../components/buttons/mainButton';
import {Theme} from "@mui/material/styles";
import Image from "../../assets/landingPage/landingVitrine.jpg";
import ImageResp from "../../assets/landingPage/landingVitrineMobile.jpg";
import Check from "../../assets/landingPage/check.svg";
import {PUBLIC_URL} from '../../common/struct/urlManager';
import {useNavigate, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {BLOCKS} from '../../common/struct/landingVar';

export function ChipLabel(props: {title: string, fontColor?: string, sx?: SxProps<Theme>}): JSX.Element {

  const {t} = useTranslation();

  return (
    <Box sx={{display: "flex", alignItems: "center", maxWidth: "100vw", overflow: "hidden", ...props.sx}}>
      {/* @ts-ignore */}
      <img src={Check} alt={t("landing_page.alt.check")} style={{height: "34px", width: "34px"}} />
      <Typography variant="body1" color={props.fontColor ?? "ornament.light"} sx={{fontWeight: "bold", fontSize: "18px", ml: 3}}>{props.title}</Typography>
    </Box>
  )
}

type RewardProps = {
  scrollTo: (block: string) => void;
}

export function DesktopReward(props: RewardProps): JSX.Element { 
  const {scrollTo} = props;

  const navigate = useNavigate();
  const {t} = useTranslation();

  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();

  const img0 = useMediaQuery('(min-width:1760px)');
  const img1 = useMediaQuery('(min-width:1650px)');
  const img2 = useMediaQuery('(min-width:1535px)');
  const img3 = useMediaQuery('(min-width:1435px)');
  const img4 = useMediaQuery('(min-width:1335px)');

  return (
    <Box sx={{height: img0 ? "850px" : img1 ? "780px" : img2 ? "780px" : "680px", mb: 4, position: "relative"}}>
      <Box sx={{display: "flex", position: "relative", zIndex: 1, flexDirection: "column", pt: img0 ? "110px" : img1 ? "80px" : img2 ? "70px" : img3 ? "60px" : "50px", pr: 5, boxSizing: "border-box", width: "38%"}}>
        <Typography variant="h1" sx={{fontWeight: "bold", fontSize: "40px", color: "ornament.light"}}>{t("landing_page.body.main_title")}</Typography>
        <Box sx={{display: "flex", flexDirection: "column", mb: img4 ? 7 : 1, mt: img4 ? 7 : 3}}>
          <ChipLabel title={t("landing_page.body.main_chip.free")} sx={{mb: 2}} />
          <ChipLabel title={t("landing_page.body.main_chip.easy")} sx={{mb: 2}}/>
          <ChipLabel title={t("landing_page.body.main_chip.infinite")} sx={{mb: 2}} />
        </Box>
        <Typography variant="body1" color="ornament.light" sx={{fontWeight: "400", fontSize: "16px"}}>
          {t("landing_page.body.main_text_1")}
        </Typography>
        <Typography variant="body1" color="ornament.light" sx={{fontWeight: "400", fontSize: "16px", mt: 4}}>
          {t("landing_page.body.main_text_2")}
        </Typography>
        <Box sx={{display: "flex", flexWrap: "wrap", mt: 6}}>
          <PrimaryBigButton action={(): void => navigate(`${PUBLIC_URL.REGISTER}?${queryString}`)} label={t("landing_page.body.main_btn_1")} sx={{textTransform: "uppercase", fontWeight: 700, backgroundColor: "#50DBB9", color: "#15154B", height: "42px", "&:hover": {backgroundColor: "ornament.main", color: "#15154B"}, "& .Mui-disabled": {backgroundColor: "#50DBB9", color: "#15154B"}}}/>
        </Box>
        <Box sx={{display: "flex", flexWrap: "wrap", mt: 1}}>
          <Typography variant="body1" color="ornament.light" sx={{fontWeight: "400", fontSize: "16px", mr: 1}}>
            {t("landing_page.body.main_text_demo")}
          </Typography>
          <Typography variant="body1" onClick={(): void => scrollTo(BLOCKS.FORM)} sx={{fontWeight: "700", fontSize: "16px", textDecoration: "underline", mt:"-1px", cursor: "pointer", color: "ornament.main", p: "0px"}}>
            {t("landing_page.body.main_btn_2")}
          </Typography>
        </Box>
      </Box>
      <Box sx={{position: "absolute", zIndex: 0, left: img0 ? "36%" : "40%", top: img0 ? "-50%" : img1 ? "-48%" : img2 ? "-46%" : img3 ? "-49%" : img4 ? "-44%" : "-40%"}}>
        <img src={Image} alt={t("landing_page.alt.front")} style={{height: img0 ? "100%" : img1 ? "1130px" : img2 ? "1060px" : img3 ? "1000px" : img4 ? "930px" : "860px", maxHeight: "1563px"}}/>
      </Box>
    </Box>
  )
}

export function MobileReward(props: RewardProps): JSX.Element {
  const {scrollTo} = props;

  const navigate = useNavigate();
  const {t} = useTranslation();

  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();

  const breakpointL = useMediaQuery('(min-width:675px)');
  const breakpointChip = useMediaQuery('(min-width:625px)');
  const breakpointM = useMediaQuery('(min-width:570px)');
  const breakpointS = useMediaQuery('(min-width:510px)');
  const breakpointXS = useMediaQuery('(min-width:460px)');
  const breakpointXXS = useMediaQuery('(min-width:400px)');

  return (
    <Box sx={{height: breakpointL ? "900px" : breakpointChip ? "880px" : breakpointM ? "950px" : breakpointS ? "900px" : breakpointXXS ? "960px" : "1100px"}}>
      <Box sx={{display: "flex", flexDirection: "column", width: "100%", maxWidth: "600px", margin: "0 auto", zIndex: 2, boxSizing: "border-box"}}>
        <Typography variant="h1" sx={{fontWeight: "bold", fontSize: "40px", color: "ornament.light"}}>{t("landing_page.body.main_title")}</Typography>
        <Box sx={{display: "flex", flexWrap: "wrap", flexDirection: breakpointXS ? "row" : "column", mt: 7, mb: "28px"}}>
          <ChipLabel title={t("landing_page.body.main_chip.free")} sx={{mr: 4, mb: 2}} />
          <ChipLabel title={t("landing_page.body.main_chip.easy")} sx={{mr: 4, mb: 2}} />
          <ChipLabel title={t("landing_page.body.main_chip.infinite")} sx={{mb: 2}} />
        </Box>
        <Typography variant="body1" color="ornament.light" sx={{fontWeight: "400", fontSize: "16px"}}>
          {t("landing_page.body.main_text_1")}
        </Typography>
        <Typography variant="body1" color="ornament.light" sx={{fontWeight: "400", fontSize: "16px", mt: 4}}>
          {t("landing_page.body.main_text_2")}
        </Typography>
        <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", mt: 5}}>
          <PrimaryBigButton action={(): void => navigate(`${PUBLIC_URL.REGISTER}?${queryString}`)} label={t("landing_page.body.main_btn_1")} sx={{textTransform: "uppercase", fontWeight: 700, backgroundColor: "#50DBB9", color: "#15154B", height: "42px", "&:hover": {backgroundColor: "ornament.main", color: "#15154B"}, "& .Mui-disabled": {backgroundColor: "#50DBB9", color: "#15154B"}}}/>
        </Box>
        <Box sx={{display: "flex", flexWrap: "wrap", mt: 2}}>
          <Typography variant="body1" color="ornament.light" sx={{fontWeight: "400", fontSize: "16px", mr: 1}}>
            {t("landing_page.body.main_text_demo")}
          </Typography>
          <Typography variant="body1" onClick={(): void => scrollTo(BLOCKS.FORM)} sx={{fontWeight: "700", fontSize: "16px", textDecoration: "underline", mt:"-1px", cursor: "pointer", color: "ornament.main", p: "0px"}}>
            {t("landing_page.body.main_btn_2")}
          </Typography>
        </Box>
      </Box>
      <Box sx={{display: "flex", justifyContent: "center", height: breakpointL ? "100%" : breakpointM ? "450px" : breakpointS ? "400px" : "350px", maxHeight: "500px", mt: 4}}>
        <img src={ImageResp} alt={t("landing_page.alt.front")} style={{height: "100%", marginLeft: "282px"}} />
      </Box>
    </Box>
  )
}
